import { useState } from 'react';
import { Group, Text } from '@mantine/core';
import { IconBoxOff } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { LocationFilterSelect, ProductFilterSelect, SiteFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { useStockOnHandReport } from '../api';
import { formatCostPerUom, formatKg, formatMoneyCents, formatQty } from '../format';
import type { StockOnHandRow } from '../types';

/** Report #1 + register "stock on hand by location" (BUILD_SPEC.md §13). Also the
 * PRODUCT search-result deep link target (`?productId=`). */
export function StockOnHandReport({ initialProductId }: { initialProductId?: string }) {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(initialProductId ?? null);

  const query = useStockOnHandReport({
    siteId: siteId ?? undefined,
    locationId: locationId ?? undefined,
    productId: productId ?? undefined,
  });
  const rows = query.data?.items ?? [];

  const columns: ReportColumn<StockOnHandRow>[] = [
    { key: 'location', header: 'Location', render: (r) => `${r.locationCode} — ${r.locationName}` },
    { key: 'product', header: 'Product', render: (r) => `${r.productSku} — ${r.productName}` },
    { key: 'qty', header: 'Qty', align: 'right', render: (r) => formatQty(r.qty) },
    { key: 'wetKg', header: 'Wet kg', align: 'right', render: (r) => formatKg(r.wetKg) },
    { key: 'dryKg', header: 'Dry kg', align: 'right', render: (r) => formatKg(r.dryKg) },
    { key: 'handlingUnits', header: 'Units', align: 'right', render: (r) => (r.handlingUnits ? String(r.handlingUnits) : '—') },
    { key: 'avgCost', header: 'Avg cost', align: 'right', render: (r) => formatCostPerUom(r.avgCostCentsPerUnit, r.baseUom) },
    { key: 'value', header: 'Value', align: 'right', render: (r) => formatMoneyCents(r.valueCents) },
  ];

  return (
    <ReportPage
      title="Stock on hand & valuation"
      description="Product x location, valued at each product's current pool weighted-average cost."
      action={
        <CsvDownloadButton
          path="/reporting/stock-on-hand"
          query={{ siteId: siteId ?? undefined, locationId: locationId ?? undefined, productId: productId ?? undefined }}
          filename="stock-on-hand.csv"
          description="Exports the stock-on-hand valuation, matching your filters, as a CSV file"
        />
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" w={220} />
        <LocationFilterSelect value={locationId} onChange={setLocationId} siteId={siteId ?? undefined} label="Location" w={220} />
        <ProductFilterSelect value={productId} onChange={setProductId} label="Product" w={240} />
      </Group>

      {query.data && (
        <Text size="sm" c="dimmed">
          Total value: <strong>{formatMoneyCents(query.data.totalValueCents)}</strong> across {rows.length} row
          {rows.length === 1 ? '' : 's'}.
        </Text>
      )}

      <ReportTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => `${r.locationId}:${r.productId}`}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage="Failed to load stock on hand."
        emptyMessage="No stock recorded for the selected filters"
        emptyHint="Try a different site, location or product."
        emptyIcon={IconBoxOff}
      />
    </ReportPage>
  );
}
